import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { Partner } from '../models/Partner';
import { DriverImport } from '../models/DriverImport';
import { VehicleImport } from '../models/VehicleImport';
import { VehicleTypeEnum } from '../models/freight-document/VehicleTypeEnum';
import { map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class CsvContentParserService {
  parsePartnerCsv(csv: string): Observable<Partner[]> {
    const defaultHeaders =
      'name,street,number,city,postal code,country code alpha 3,email(optional),EORI(optional),taxid(optional),taxnumber(optional)';
    return this.parseCsvToLinesArray(csv, 6, defaultHeaders).pipe(
      map((csvItems) => csvItems.map((csvItem) => this.csvItemToPartner(csvItem)))
    );
  }

  parseDriverCsv(csv: string): Observable<DriverImport[]> {
    const defaultHeaders =
      'last name,first name,email(optional),phone prefix(optional),phone(optional),username(optional),password(optional)';
    // one required field, not two: a row missing its first name is kept so the API can name it,
    // rather than being silently dropped here and quietly shrinking the import
    return this.parseCsvToLinesArray(csv, 1, defaultHeaders).pipe(
      map((csvItems) => csvItems.map((csvItem) => this.csvItemToDriverImport(csvItem)))
    );
  }

  parseVehicleCsv(csv: string): Observable<VehicleImport[]> {
    const defaultHeaders = 'type,license plate,vin(optional),username(optional),password(optional),special circulation permit(optional)';
    // one required field, not two: a row missing its license plate is kept so the API can name it,
    // rather than being silently dropped here and quietly shrinking the import
    return this.parseCsvToLinesArray(csv, 1, defaultHeaders).pipe(
      map((csvItems) => csvItems.map((csvItem) => this.csvItemToVehicleImport(csvItem)))
    );
  }

  private csvItemToVehicleImport(data: string[]): VehicleImport {
    return {
      // upper-cased so a hand-typed "truck" is accepted; an unknown value is rejected by the API
      type: data[0]?.trim().toUpperCase() as VehicleTypeEnum,
      licensePlateNumber: this.valueOrNull(data[1]),
      vehicleIdentificationNumber: this.valueOrNull(data[2]),
      username: this.valueOrNull(data[3]),
      password: this.valueOrNull(data[4]),
      specialCirculationPermitNumber: this.valueOrNull(data[5]),
    };
  }

  private csvItemToDriverImport(data: string[]): DriverImport {
    return {
      lastName: this.valueOrNull(data[0]),
      firstName: this.valueOrNull(data[1]),
      email: this.valueOrNull(data[2]),
      phoneNumberPrefix: this.valueOrNull(data[3]),
      phoneNumber: this.valueOrNull(data[4]),
      username: this.valueOrNull(data[5]),
      password: this.valueOrNull(data[6]),
    };
  }

  private valueOrNull(value: string): string | null {
    return value?.trim() ? value.trim() : null;
  }

  private csvItemToPartner(data: string[]): Partner {
    return {
      name: data[0],
      streetName: data[1],
      buildingNumber: data[2],
      cityName: data[3],
      postalCode: data[4],
      countryCode: data[5],
      email: data[6] ? data[6] : null,
      traderIdentificationNumber: data[7] ? data[7] : null,
      taxIdentificationNumberType: data[8] ? data[8] : null,
      taxIdentificationNumber: data[9] ? data[9] : null,
    };
  }

  private parseCsvToLinesArray(csv: string, minimalRequiredFields: number, defaultHeader: string): Observable<string[][]> {
    const linesSubject = new BehaviorSubject<string[][]>([]);
    const lines = csv.split(/\r\n|\n/)?.filter((lineData) => lineData?.trim().length);
    const separator = this.detectSeparator(lines);
    const headerFields = defaultHeader.split(',');

    const result = lines
      .map((line) => this.splitLine(line, separator))
      .filter(
        (data) => data.length >= minimalRequiredFields && !this.isHeaderLine(data, headerFields) && !this.isEmptyLine(data)
      );

    linesSubject.next(result);
    return linesSubject.asObservable();
  }

  /**
   * Minimal RFC 4180 splitting: a value wrapped in double quotes may contain the separator, and a doubled
   * quote inside such a value is an escaped quote. Spreadsheets quote exactly the cells that need it, so a
   * plain split would shift every later column of those rows.
   */
  private splitLine(line: string, separator: string): string[] {
    const fields: string[] = [];
    let field = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const character = line[i];
      if (character === '"') {
        if (inQuotes && line[i + 1] === '"') {
          field += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (character === separator && !inQuotes) {
        fields.push(field);
        field = '';
      } else {
        field += character;
      }
    }
    fields.push(field);
    return fields;
  }

  /**
   * Picked once for the whole file rather than per line, since splitting each line on the first separator
   * that yields enough fields mis-reads a semicolon file whose values contain a comma. Counted over every
   * line so that one unusual first line cannot decide the whole file.
   */
  private detectSeparator(lines: string[]): string {
    const supportedSeparators = [',', ';'];
    return supportedSeparators.reduce((widest, candidate) =>
      this.countFields(lines, candidate) > this.countFields(lines, widest) ? candidate : widest
    );
  }

  private countFields(lines: string[], separator: string): number {
    return lines.reduce((total, line) => total + this.splitLine(line, separator).length, 0);
  }

  /**
   * Matched on the leading columns only, so a header still gets skipped when the "(optional)" hints have
   * been edited away or trailing columns dropped.
   */
  private isHeaderLine(data: string[], headerFields: string[]): boolean {
    const columnsToMatch = Math.min(2, headerFields.length);
    return (
      data.length >= columnsToMatch &&
      headerFields
        .slice(0, columnsToMatch)
        .every((headerField, index) => this.normalise(data[index]) === this.normalise(headerField))
    );
  }

  private isEmptyLine(data: string[]): boolean {
    return data.every((field) => !field?.trim());
  }

  private normalise(field: string): string {
    return field?.trim().toLowerCase();
  }
}
